import {
  destDetailAtom,
  mapZoomLevelAtom,
  MARKER_VIEW_ZOOM,
  polyLineOverAtom,
  shippingCardOverAtom,
} from '@/commons/constants';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Marker } from 'react-naver-maps';
import { tooltipAtom } from '.';
import { Dest, TimeLine, TimeLineItem } from '@/types/result';

export default function RoutingMarker({ list }: { list: TimeLine[] }) {
  const setTooltip = useSetAtom(tooltipAtom);

  const [polyLineOver, setPolyLineOver] = useAtom(polyLineOverAtom);
  const [destDetail, setDestDetail] = useAtom(destDetailAtom);

  const mapZoomLevel = useAtomValue(mapZoomLevelAtom);
  const shippingCardOver = useAtomValue(shippingCardOverAtom);

  const [highlightList, setHilightList] = useState<number[]>([]);

  useEffect(() => {
    //활성화(highlight) 대상 선정
    const temp: number[] = [];
    polyLineOver && temp.push(polyLineOver); //경로에 마우스 오버
    shippingCardOver && temp.push(shippingCardOver); //목록 마우스 오버
    setHilightList(temp);
  }, [polyLineOver, shippingCardOver]);

  const handleMarkerClick = useCallback(
    (carSeq: number, destId: number) => {
      const targetShipping = list.find((lItem: TimeLine) => lItem.car.car_seq === carSeq);
      if (targetShipping) {
        const target = targetShipping.destList.find((dItem: Dest) => dItem.id === destId);
        setDestDetail({ car: targetShipping.car, color: targetShipping.color, dest: target });
      }
    },
    [list, setDestDetail],
  );

  const handleMarkerOver = useCallback(
    (carSeq: number) => {
      setPolyLineOver(carSeq);
    },
    [setPolyLineOver],
  );

  const handleMarkerOut = useCallback(() => {
    setTooltip(undefined);
    setPolyLineOver(undefined);
  }, [setTooltip, setPolyLineOver]);

  return (
    <>
      {list &&
        list.length > 0 &&
        list.map((carItem: TimeLine) =>
          carItem?.timelineList
            ?.filter((v) => v.target)
            ?.map((pItem: TimeLineItem, pIndex: number) => {
              //활성화 대상 체크
              const check =
                highlightList.length > 0 && highlightList.findIndex((fItem) => fItem === carItem.car.car_seq) > -1;
              //클릭한 대상체크(팝업에 표시하는 대상(목적지)은 더크게 표시함)
              const selected = destDetail?.dest.id === pItem.target.id;
              return (
                (mapZoomLevel > MARKER_VIEW_ZOOM || pIndex === 0 || list.length === 1 || check) && (
                  <DestMarker
                    key={carItem.car.car_seq + '_' + pItem.target.id}
                    carSeq={carItem.car.car_seq}
                    destId={pItem.target.id}
                    position={pItem.target.coord ?? undefined}
                    color={pItem.color}
                    deliveryNo={pItem.target.delivery_no}
                    zIndex={100 + (selected ? pIndex : 0)}
                    selected={selected}
                    dimmed={highlightList.length > 0 && !check}
                    onMarkerClick={handleMarkerClick}
                    onMarkerOver={handleMarkerOver}
                    onMarkerOut={handleMarkerOut}
                  />
                )
              );
            }),
        )}
    </>
  );
}

interface DestMarkerProps {
  carSeq: number;
  destId: number;
  position: naver.maps.Coord | undefined;
  color: string;
  deliveryNo: number | string;
  zIndex: number;
  selected: boolean;
  dimmed: boolean;
  onMarkerClick: (carSeq: number, destId: number) => void;
  onMarkerOver: (carSeq: number) => void;
  onMarkerOut: () => void;
}

// 줌 레벨이 바뀔 때마다 RoutingMarker 전체가 리렌더되므로, 실제로 값이 바뀐 마커만
// icon 객체를 새로 만들고 네이버맵에 setOptions를 태우도록 마커 단위로 memo 처리한다.
const DestMarker = memo(function DestMarker({
  carSeq,
  destId,
  position,
  color,
  deliveryNo,
  zIndex,
  selected,
  dimmed,
  onMarkerClick,
  onMarkerOver,
  onMarkerOut,
}: DestMarkerProps) {
  const icon = useMemo(
    () => ({
      content: `<div style='display:flex;opacity:${dimmed ? 0.4 : 1};${selected ? 'font-weight:800;font-size:1rem;' : ''}width:${selected ? 1 : 0.8}rem;height:${selected ? 1 : 0.8}rem;justify-content:center;align-items:center;border-radius:25px;padding:0.25rem;background:${color};color:#ffffff'>${deliveryNo}</div>`,
    }),
    [dimmed, selected, color, deliveryNo],
  );

  const handleClick = useCallback(() => onMarkerClick(carSeq, destId), [onMarkerClick, carSeq, destId]);
  const handleOver = useCallback(() => onMarkerOver(carSeq), [onMarkerOver, carSeq]);

  return (
    <Marker
      zIndex={zIndex}
      icon={icon}
      position={position}
      onClick={handleClick}
      onMouseover={handleOver}
      onMouseout={onMarkerOut}
    />
  );
});
